import {
    NORTHSTAR_TEMPLATE_REPLAY_CRITERIA,
    NORTHSTAR_TEMPLATE_REPLAY_ORGANIZATION,
    NORTHSTAR_TEMPLATE_REPLAY_VERSION,
    TemplateFixtureReplay,
} from '../apiClients/TemplateFixtureReplayClient';
import { ROLE_TEMPLATE_REPLAY_ORGANIZATION_IDS } from '../Environment';

export const NORTHSTAR_ROLE_TEMPLATE_REPLAY_VERSION = 'northstar-role-to-template-v1';
export const NORTHSTAR_ROLE_TEMPLATE_SCENARIO_NAME = 'Field Service Technician role-to-template capture';
export const NORTHSTAR_ROLE_TEMPLATE_NAME = 'Field Service Technician Success Profile';
export const NORTHSTAR_ROLE_TEMPLATE_GROUPS = Object.freeze([
    'Sustainable',
    'Performant',
    'Adaptable',
    'Communication',
    'Culture',
] as const);

export interface RoleTemplateReplayActivity {
    Title: string;
    Description: string;
}

export interface RoleTemplateReplay {
    Version: string;
    IsSynthetic: true;
    ReadOnly: true;
    OrganizationId: string;
    OrganizationName: string;
    PositionTitle: string;
    Activities: RoleTemplateReplayActivity[];
    Talent: {
        Name: string;
        Email: string;
        PositionTitle: string;
        AssessorName: string;
        AssessorEmail: string;
    };
    TemplateStatus: 'Active';
    Template: TemplateFixtureReplay & { ProficiencyGroups: readonly string[] };
}

export const NORTHSTAR_ROLE_TEMPLATE_ACTIVITIES: RoleTemplateReplayActivity[] = Object.freeze([
    Object.freeze({
        Title: 'Inspect commercial equipment',
        Description: 'Assess operating condition, identify wear, defects, and safety concerns, and determine next steps.',
    }),
    Object.freeze({
        Title: 'Perform preventive maintenance',
        Description: 'Follow established service procedures, manufacturer guidance, and site requirements.',
    }),
    Object.freeze({
        Title: 'Document service findings',
        Description: 'Record completed work, parts used, observed issues, and recommended follow-up clearly.',
    }),
    Object.freeze({
        Title: 'Communicate safety risks',
        Description: 'Escalate service limitations and unresolved issues promptly to supervisors and customers.',
    }),
]) as unknown as RoleTemplateReplayActivity[];

const cloneActivities = () => NORTHSTAR_ROLE_TEMPLATE_ACTIVITIES.map(activity => ({ ...activity }));
const cloneCriteria = () => NORTHSTAR_TEMPLATE_REPLAY_CRITERIA.map(criterion => ({ ...criterion }));

export const isRoleTemplateReplayOrganizationAllowed = (
    selectedOrganizationId: string,
    selectedOrganizationName: string,
    allowedOrganizationIds: readonly string[] = ROLE_TEMPLATE_REPLAY_ORGANIZATION_IDS,
) => selectedOrganizationId.length > 0
    && allowedOrganizationIds.includes(selectedOrganizationId)
    && selectedOrganizationName === NORTHSTAR_TEMPLATE_REPLAY_ORGANIZATION;

/**
 * A local GET-only fixture service for production-safe capture. It deliberately
 * has no ApiBase dependency, network request, or mutation method.
 */
export default class RoleTemplateReplayService {
    private readonly allowedOrganizationIds: readonly string[];

    constructor(allowedOrganizationIds: readonly string[] = ROLE_TEMPLATE_REPLAY_ORGANIZATION_IDS) {
        this.allowedOrganizationIds = Object.freeze([...allowedOrganizationIds]);
    }

    async Get(
        selectedOrganizationId: string,
        selectedOrganizationName: string,
        signal?: AbortSignal,
    ): Promise<RoleTemplateReplay> {
        if (signal?.aborted)
            throw new DOMException('The replay request was aborted.', 'AbortError');
        if (!isRoleTemplateReplayOrganizationAllowed(
            selectedOrganizationId,
            selectedOrganizationName,
            this.allowedOrganizationIds,
        ))
            throw new Error('The role-to-template replay is available only in the synthetic Northstar organization.');

        const template: RoleTemplateReplay['Template'] = {
            Version: NORTHSTAR_TEMPLATE_REPLAY_VERSION,
            IsSynthetic: true,
            ReadOnly: true,
            OrganizationId: selectedOrganizationId,
            OrganizationName: NORTHSTAR_TEMPLATE_REPLAY_ORGANIZATION,
            ScenarioName: NORTHSTAR_ROLE_TEMPLATE_SCENARIO_NAME,
            TemplateName: NORTHSTAR_ROLE_TEMPLATE_NAME,
            ProficiencyGroups: [...NORTHSTAR_ROLE_TEMPLATE_GROUPS],
            Criteria: cloneCriteria(),
            AbilityLegend: ['Unskilled', 'Beginner', 'Intermediate', 'Advanced', 'Expert'],
            BehaviorLegend: ['Never', 'Rarely', 'Sometimes', 'Usually', 'Always'],
        };

        return {
            Version: NORTHSTAR_ROLE_TEMPLATE_REPLAY_VERSION,
            IsSynthetic: true,
            ReadOnly: true,
            OrganizationId: selectedOrganizationId,
            OrganizationName: NORTHSTAR_TEMPLATE_REPLAY_ORGANIZATION,
            PositionTitle: 'Field Service Technician',
            Activities: cloneActivities(),
            Talent: {
                Name: 'Jordan Avery',
                Email: 'jordan.avery@example.invalid',
                PositionTitle: 'Field Service Technician',
                AssessorName: 'Northstar Trainer',
                AssessorEmail: 'northstar.trainer@example.invalid',
            },
            TemplateStatus: 'Active',
            Template: template,
        };
    }
}
